import React, { useCallback, useEffect, useState } from "react";
import { CircleCheck, HeartHandshake, Search } from "lucide-react";
import { exportCareGapsPatients, fetchCareGapsPatients } from "../../services/caregaps";
import { useLocation, useNavigate } from "react-router-dom";
import { can_visit, caregap_name, debounce, generatePatienCareGaptTableData, getDateString, getPractice, getSpeciality, notify, numberWithCommas, removeSecureItem, userMessageTemplates } from "../../helpers";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { fetchFilterData } from "../../services/saveFilter";
import { addToWaitingRoom } from "../../services/patients";
import { getCaregapAdditionalData } from "../../Session";
import DataTablePagination from "../DataTablePagination";
import Filter from "../Filter";
import FilterSummary from "../UI/FilterSummary";
import Spacing from "../Spacing";
import CareGapPlusDataTable from "../DataTable/CareGapPlusDataTable";
import SaveFilter from "../Filter/SaveFilter";
import FilterSaveConfirmationModal from "../Modal/FilterSaveConfirmationModal";
import Grid from "../Grid";

export default function CareGapPlus({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit, setLetterLimit, changePracticeSpeciality, waitingRoomProviders}) {
  const [additionalData, setAdditionalData] = useState(null);
  
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let specilityId = getSpeciality()?.id || 32;
  let dateStr = getDateString();

  useEffect(() => {
    const handlePracticeChange = async () => {
      if (selectedPracticeChange != null && fetchChangePracticeData) {
        location_id = selectedPracticeChange?.location_id;
        account_id = selectedPracticeChange?.account_id;
        await removeSecureItem("caregapAdditionalData");
        specilityId = (can_visit('ehr_veradigm')) ? 32 : selectedPracticeChange?.specialities?.[0]?.id || 32;
      }
    };

    handlePracticeChange();
  }, [selectedPracticeChange, fetchChangePracticeData]);

  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 20; }

  const [buttonLoader, setButtonLoader] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [caregappatients, setCaregapPatients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [messageTemplate] = useState(userMessageTemplates());
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const parseQualityMeasure = () => {
    try {
      return (
        JSON.parse(
          new URLSearchParams(location.search).get("quality_measure")
        ) || []
      );
    } catch (error) {
      return [];
    }
  };

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    insurance_ids: [],
    last_visit: "",
    visit_day_query_type: "",
    visited_last_days: "",
    not_visited_last_days: "",
    quality_measure: parseQualityMeasure(),
    order_by: [],
    city: "",
    state: "",
    zip: "",
  };
  const [filter, setFilter] = useState(defaultFilter);

  useEffect(() => {
    const savedData = getCaregapAdditionalData();
    if (savedData) {
      setAdditionalData(savedData);
    }
  }, [location.search]);

  const DefaultValues = {
    page: 1,
    per_page: 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    mobile_phone: "",
    start_age: "",
    end_age: "",
    insurance_ids: [],
    last_visit: "",
    visit_day_query_type: "",
    visited_last_days: "",
    not_visited_last_days: "",
    quality_measure: [],
    city: "",
    state: "",
    zip: "",
    dos: "",
  };

  const [CaregappatientsCount, setCaregapPatientsCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setCaregapPatients((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const [isFilterCleared, setIsFilterCleared] = useState(false);

  const handleClearFilters = () => {
    setIsFilterCleared(true);
  };

  const handleResetFilterStatus = () => {
    setIsFilterCleared(false);
  };

  const [selectedInsuranceOptions, setSelectedInsuranceOptions] = useState([]);

  // Function to handle insurance selection and update selected options
  const handleInsuranceChange = (insuranceId, checked) => {
    if (checked) {
      setSelectedInsuranceOptions((prev) => [...prev, insuranceId]); // Add
    } else {
      setSelectedInsuranceOptions((prev) => prev.filter((id) => id !== insuranceId)); // Remove
    }
  };

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",  // Clear the specified field in filter state
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",  // Clear the specified field in patientFormData
    }));
  };
  const [patientFormData, setPatientFormData] = useState(DefaultValues);
  const [showSave, setShowSave] = useState(true);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [filterData, setFilterData] = useState([]);

  /**
   * Handles the select all functionality for patients.
   */
  // const handleSelectAll = () => {
  //   const newCheckedIds = selectAll
  //     ? []
  //     : caregappatients.map((item) => item.id);
  //   setCheckedIds(newCheckedIds);

  //   const newData = caregappatients.map((row) => ({
  //     ...row,
  //     checked: !selectAll,
  //   }));
  //   setCaregapPatients(newData);
  //   setSelectAll(!selectAll);
  // };
  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setCaregapPatients((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  useEffect(() => {
    loadData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    const qualityMeasure = getFilter.quality_measure;
    if (qualityMeasure?.length === 1) {
      const updatedQualityMeasure = JSON.stringify([
        { id: qualityMeasure[0]?.id, status: qualityMeasure[0]?.status },
      ]);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("quality_measure", updatedQualityMeasure);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }

    try {
      const response = await fetchCareGapsPatients(getFilter);
      const generateData = generatePatienCareGaptTableData(response.data.results);
      setCaregapPatients(generateData);
      setCaregapPatientsCount({
        totalCount: response.data.count,
        totalPages: response.data.total_pages,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.error("Failed to load care gaps patients data", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  /**
   * Handles the page change event.
   *
   * @param {number} newPage - The new page number.
   */
  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  /**
   * Handles the change of rows per page in the filter.
   *
   * @param {number} value - The new value for rows per page.
   */
  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
      page: 1,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const handleExportAll = async (e) => {
    setButtonLoader(true);
    try {
      const response = await exportCareGapsPatients(filter);

      if (response && response.data) {
        const responseData = response.data;
        const dataBlob = new Blob([responseData], {
          type: "text/csv;charset=utf-8;",
        });
        const generateURL = window.URL.createObjectURL(dataBlob);

        // File Name Generate
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const filename = `Patient_Data_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.csv`;

        const link = document.createElement("a");
        link.href = generateURL;
        link.setAttribute("download", filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        window.URL.revokeObjectURL(generateURL);
      }
    } catch (error) {
      // console.log("🚀 ~ ExporPatients ~ error:", error);
    } finally {
      setButtonLoader(false);
    }
  };

  const loadDefault = () => {
    try {
      const qualityMeasure = parseQualityMeasure();
      if (qualityMeasure.length === 0) {
        setFilter((prevFilter) => ({
          ...prevFilter,
          quality_measure: [],
          search_term: "",
          first_name: "",
          last_name: "",
          dob: "",
          sex: "",
          mobile_phone: "",
          start_age: "",
          end_age: "",
          insurance_ids: [],
          last_visit: "",
          visit_day_query_type: "",
          visited_last_days: "",
          not_visited_last_days: "",
          order_by: [],
          city: "",
          state: "",
          zip: "",
        }));
      }
    } catch (error) { }
  };

  useEffect(() => {
    loadDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const checkboxes = [
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Done" },
  ];

  const handleFilterCheckboxChange = (value) => {
    setDataLoading(true);
    setShowSave(true);
    setFilter((prevFilter) => ({
      ...prevFilter,
      quality_measure: [{ id: parseQualityMeasure()[0]?.id, status: value }],
      page: 1,
    }));
    setPatientFormData({ ...filter, quality_measure: [{ id: parseQualityMeasure()[0]?.id, status: value }], page: 1 });
  };

  var pageTitle = "Care Gap Plus";
  if(location?.search && filter?.quality_measure?.length === 1) {
    var title_status = filter?.quality_measure[0]?.status;
    pageTitle = `${caregap_name(filter?.quality_measure[0]?.id)} - ${title_status.charAt(0).toUpperCase() + title_status.slice(1)}`;
  }

  const loadFilters = async () => {
    const filters = {
      account_id: account_id,
      location_id: location_id,
      user_id: user_id,
      filter_type: "caregap-plus",
    };

    try {
      const response = await fetchFilterData(filters);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          setFilterData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ loadFilters ~ error:", error);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  const handleAddToWaitingRoom = async (data, id) => {
    const postDataObject = {
      account_id: account_id,
      location_id: location_id,
      speciality_id: specilityId,
      patient_id: data.id,
      dos: dateStr,
      care_gap: [],
      review_sent: false,
      provider_id: id
    };

    try {
      await addToWaitingRoom(postDataObject);
      notify("success", "Patient added to waiting room successfully");
    } catch (error) {
      notify("error", error?.response?.data?.detail);
    }
  };

  const handleReferralNavigation = (data) => {
    navigate(`/new-referral`, { state: { data } });
  };

  useDocumentTitle(`${pageTitle}`);

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_cyan_bg docpro_white_color">
            <HeartHandshake width={20} height={20} />
          </i>
          Care Gap
          <span>{numberWithCommas(CaregappatientsCount.totalCount)}</span>
        </h2>
      </div>
      {additionalData &&
        filter?.quality_measure?.length === 1 &&
        getCaregapAdditionalData() && (
          <Grid variant="docpro_grid_gap_4 docpro_grid_col_4">
            <div className="docpro_card docpro_border_card">
              <div className="w-full">
                <img
                  src={`/images/icons/${additionalData?.icon}.svg`}
                  width={30}
                  alt="Icon"
                />
                <Spacing lg="4" sm="4" />
                <h3 className="docpro_fs_16 docpro_mb_0">
                  {additionalData?.title}
                </h3>
                <p>{additionalData?.sub_title}</p>

                {additionalData?.completed ? (
                  <CircleCheck
                    width={20}
                    height={20}
                    style={{ color: "#12A594" }}
                  />
                ) : (
                  <>
                    <div className="docpro_progress_title docpro_fs_12 pt-1">
                      <span>
                        {additionalData?.done_percentage +
                          "% Done (" +
                          additionalData?.total_pending_item +
                          " patients remaining)"}
                      </span>
                    </div>
                    <Spacing lg="4" sm="4" />
                    <div className="docpro_progress">
                      <div
                        className="docpro_progress_in"
                        style={{
                          width: `${
                            additionalData?.done_percentage
                              ? additionalData?.done_percentage
                              : 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Grid>
        )}
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings small_heading">
          <div className="searching_div">
            <form className="docpro_search_form width_100">
              <input
                type="search"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>

            {location?.search && filter?.quality_measure?.length === 1 && (
              <div className="filter_type small_filter_type">
                {checkboxes.map((checkbox) => (
                  <div
                    key={checkbox.value}
                    className="docpro_checkbox"
                    style={{ marginLeft: checkbox.value === 1 ? 0 : "12px" }}
                  >
                    <input
                      type="checkbox"
                      name="status"
                      value={checkbox.value}
                      checked={filter.quality_measure.some(
                        (measure) => measure.status === checkbox.value
                      )}
                      onChange={() =>
                        handleFilterCheckboxChange(checkbox.value)
                      }
                    />
                    <label style={{ marginLeft: "5px" }}>
                      {checkbox.label}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={`docpro_data_table_btns p-0 ${
              location?.search && filter?.quality_measure?.length === 1
                ? "small_filter_btns"
                : "small_filter_btns3"
            }`}
          >
            {filterData?.length > 0 && (
              <SaveFilter
                account_id={account_id}
                location_id={location_id}
                user_id={user_id}
                filterData={filterData}
                setDataLoading={setDataLoading}
                setPatientFormData={setPatientFormData}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                handleFilterCheckboxChange={handleFilterCheckboxChange}
                setSelectedMeasures={setSelectedMeasures}
                setFilter={setFilter}
                setShowSave={setShowSave}
                loadFilters={loadFilters}
                reloadData={loadData}
                reloadDataFilter={defaultFilter}
                previousScrollPosition={previousScrollPosition}
                setPreviousScrollPosition={setPreviousScrollPosition}
              />
            )}
            <div className="docpro_toor_box5">
              <Filter
                filterType="patient_caregap"
                reloadData={loadData}
                reloadDataFilter={filter}
                setDataLoading={setDataLoading}
                parseQualityMeasure={parseQualityMeasure}
                isFilterCleared={isFilterCleared}
                onResetFilterStatus={handleResetFilterStatus}
                selectedInsuranceOptions={selectedInsuranceOptions}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                handleInsuranceChange={handleInsuranceChange}
                patientFormData={patientFormData}
                setPatientFormData={setPatientFormData}
                selectedMeasures={selectedMeasures || []}
                setSelectedMeasures={setSelectedMeasures}
                setShowSave={setShowSave}
              />
            </div>
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary
            filter={filter}
            setFilter={setFilter}
            loadData={loadData}
            onClearFilters={handleClearFilters}
            setSelectedInsuranceOptions={setSelectedInsuranceOptions}
            clearFilterField={clearFilterField}
            selectedMeasures={selectedMeasures}
            setSelectedMeasures={setSelectedMeasures}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            setSaveConfirmModal={setSaveConfirmModal}
            showSave={showSave}
            setShowSave={setShowSave}
            setPreviousScrollPosition={setPreviousScrollPosition}
            setDataLoading={setDataLoading}
          />
        </div>

        <CareGapPlusDataTable
          tableData={caregappatients}
          handleSelectAll={() => handleSelectAll(caregappatients)}
          handleCheckboxChange={handleCheckboxChange}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          setLetterLimit={setLetterLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          exportData={handleExportAll}
          buttonLoader={buttonLoader}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          handleAddToWaitingRoom={handleAddToWaitingRoom}
          handleReferralNavigation={handleReferralNavigation}
          user_id={user_id}
          waitingRoomProviders={waitingRoomProviders}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {caregappatients.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={CaregappatientsCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={CaregappatientsCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {saveConfirmModal && (
        <FilterSaveConfirmationModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          filter_type="caregap-plus"
          filter={filter}
          openModal={saveConfirmModal}
          setOpenModal={setSaveConfirmModal}
          setShowSave={setShowSave}
          loadFilters={loadFilters}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
